import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';

export const Route = createFileRoute('/privacy-policy')({
  component: PrivacyPolicyPage,
});

function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          돌아가기
        </Link>

        <h1 className="text-2xl font-bold mb-2">개인정보처리방침</h1>
        <p className="text-sm text-muted-foreground mb-10">최종 수정일: 2026년 4월 3일</p>

        <div className="space-y-10 text-sm leading-relaxed text-foreground/90">
          <Section title="1. 수집하는 정보">
            <p>
              &quot;내 고객 신호등&quot;(이하 &quot;본 서비스&quot;)은 카페24 쇼핑몰 운영자가 설치
              시 동의한 권한 범위 내에서 다음 정보를 처리합니다.
            </p>
            <Table
              headers={['수집 항목', '출처', '목적']}
              rows={[
                [
                  '주문 번호, 주문 일시, 결제 금액',
                  '카페24 주문 API',
                  'RFM 분석 및 고객 세그먼트 산출',
                ],
                ['회원 ID (member_id)', '카페24 주문 API', '고객별 구매 패턴 분석'],
                ['쇼핑몰 ID (mall_id)', '카페24 앱 설치', '멀티 쇼핑몰 데이터 분리'],
              ]}
            />
            <p className="mt-3 text-muted-foreground">
              본 서비스는 고객의 이름, 이메일, 전화번호, 주소 등 직접적인 개인 식별 정보를 수집하지
              않습니다.
            </p>
          </Section>

          <Section title="2. 수집하지 않는 정보">
            <p>다음 정보는 카페24 API 권한을 요청하지 않으며, 수집 및 저장하지 않습니다.</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>고객 이름, 이메일, 연락처, 주소 등 개인 식별 정보</li>
              <li>주문 상품 상세 (상품명, 옵션 등)</li>
              <li>결제 수단 정보 (카드번호 등)</li>
            </ul>
          </Section>

          <Section title="3. 정보의 이용 목적">
            <ul className="list-disc pl-5 space-y-1">
              <li>RFM(Recency, Frequency, Monetary) 기반 고객 세그먼트 분석</li>
              <li>고객 생애가치(LTV) 예측</li>
              <li>구매주기 분석 및 재구매 예측</li>
              <li>코호트 기반 재구매율 추이 분석</li>
              <li>세그먼트별 자동 쿠폰 발급 (Pro 이상 요금제)</li>
            </ul>
          </Section>

          <Section title="4. 정보의 보유 및 파기">
            <Table
              headers={['구분', '보유 기간', '파기 방법']}
              rows={[
                ['카페24 인증 토큰', '앱 설치 기간 동안', '앱 삭제 시 즉시 폐기'],
                ['주문 데이터', '앱 삭제 후 30일', '보유 기간 경과 후 자동 삭제'],
                ['분석 결과 (RFM, LTV 등)', '앱 삭제 후 30일', '보유 기간 경과 후 자동 삭제'],
              ]}
            />
            <p className="mt-3 text-muted-foreground">
              앱 삭제 시 카페24 인증 토큰과 세션 정보는 즉시 파기됩니다. 주문 데이터 및 분석 결과는
              재설치 시 서비스 연속성을 위해 30일간 보유 후 파기됩니다. 즉시 삭제를 원하시는 경우
              아래 연락처로 요청해 주세요.
            </p>
          </Section>

          <Section title="5. 제3자 제공">
            <p>
              본 서비스는 수집한 정보를 제3자에게 제공하지 않습니다. 다만, 오류 모니터링을 위해
              Sentry에 익명화된 기술 로그(에러 스택, 브라우저 정보)가 전송될 수 있습니다.
            </p>
          </Section>

          <Section title="6. 정보주체의 권리">
            <ul className="list-disc pl-5 space-y-1">
              <li>
                쇼핑몰 운영자는 언제든지 카페24 앱스토어에서 본 앱을 삭제하여 데이터 처리를 중단할
                수 있습니다.
              </li>
              <li>앱 삭제 시 본 서비스에 저장된 모든 데이터는 즉시 파기됩니다.</li>
              <li>데이터 삭제 또는 처리 중단을 요청하려면 아래 연락처로 문의해 주세요.</li>
            </ul>
          </Section>

          <Section title="7. 연락처">
            <p>
              서비스 제공자: 스텝언(Stepeon)
              <br />
              이메일: ldu1020@stepeon.com
              <br />
              연락처: 010-4659-1944
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-base font-semibold mb-3">{title}</h2>
      {children}
    </section>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="overflow-x-auto mt-3">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr>
            {headers.map((h) => (
              <th key={h} className="text-left p-2 border-b font-medium bg-muted/50">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.join('-')}>
              {row.map((cell, i) => (
                <td key={`${cell}-${headers[i]}`} className="p-2 border-b">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
